import { Stack, Typography, useTheme } from "@mui/material";
import { BarChart3, IdCard, ListTodo, Pencil } from "lucide-react";
import type { FC } from "react";
import { useNavigate } from "react-router-dom";
import {
	type FinancesNavigationState,
	FinancesOrigin,
} from "web_myplann/Finances/Types/FinancesNavigationState";
import { Routing } from "web_myplann/Infrastructure/Routing/Routing";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { AppRouting } from "web_myplann/Infrastructure/Utils/UrlUtils";
import { MissingAdvisor } from "web_myplann/Profile/Components/MissingAdvisor";
import { ProfileAccountRow } from "web_myplann/Profile/Components/ProfileAccountRow";
import { ProfileAvatar } from "web_myplann/Profile/Components/ProfileAvatar";
import { ProfileSection } from "web_myplann/Profile/Components/ProfileSection";
import { ProfileToolRow } from "web_myplann/Profile/Components/ProfileToolRow";
import { useProfileOverview } from "web_myplann/Profile/Hooks/useProfileOverview";
import { LogOutRow } from "web_myplann/Shared/Components/LogOutRow";
import { MyPlannLoading } from "web_myplann/Shared/Components/MyPlannLoading";
import { MyPlannLayout } from "web_myplann/Shared/Header/MyPlannLayout";
import { MyPlannNavigationHeader } from "web_myplann/Shared/Header/MyPlannNavigationHeader";

const R = Resources.Profile;

export const ProfileV2Page: FC = () => {
	const theme = useTheme();
	const { t } = useResource();
	const navigate = useNavigate();

	const { isLoading, clientNotFound, name, email, photo, accountItems } =
		useProfileOverview();

	return (
		<MyPlannLayout
			disableContentPadding
			bgColor={theme.palette.grey[50]}
			topBar={
				<MyPlannNavigationHeader
					title={t(R.title)}
					bgColor={theme.palette.background.appBar}
					defaultRoute={AppRouting.MyPlann.Dashboard.path()}
					alwaysUseDefaultRoute
					endComponent={
						<Pencil
							size={theme.iconSize.md}
							cursor="pointer"
							color={theme.palette.grey[700]}
							onClick={() =>
								navigate(AppRouting.MyPlann.Profile.Settings.path())
							}
						/>
					}
				/>
			}
		>
			{isLoading ? (
				<MyPlannLoading />
			) : clientNotFound ? (
				<MissingAdvisor />
			) : (
				<>
					<Stack
						bgcolor={theme.palette.background.paper}
						py={3.2}
						alignItems="center"
					>
						<ProfileAvatar name={name} photo={photo} mb={1.6} />
						<Typography variant="h2">{name}</Typography>
						{email && (
							<Typography variant="body2" mt={0.4}>
								{email}
							</Typography>
						)}
					</Stack>

					<ProfileSection title={t(R.Overview.accountSection)}>
						{accountItems.map((item, index) => (
							<ProfileAccountRow
								key={item.id}
								icon={item.icon}
								label={item.label}
								value={item.value}
								actionIcon={item.actionIcon}
								onClick={item.onClick}
								showDivider={index < accountItems.length - 1}
							/>
						))}
					</ProfileSection>

					<ProfileSection title={t(R.Overview.toolsSection)}>
						<ProfileToolRow
							icon={BarChart3}
							iconBg={theme.palette.green[100]}
							iconColor={theme.palette.green[600]}
							label={t(R.Sections.FinancialBalance)}
							onClick={() =>
								navigate(AppRouting.MyPlann.Finances.Intro.path(), {
									state: {
										from: FinancesOrigin.Profile,
									} satisfies FinancesNavigationState,
								})
							}
							showDivider
						/>
						<ProfileToolRow
							icon={IdCard}
							iconBg={theme.palette.indigo[100]}
							iconColor={theme.palette.indigo[600]}
							label={t(R.Overview.documents)}
							onClick={() =>
								navigate(AppRouting.MyPlann.Profile.Paperwork.path())
							}
							showDivider
						/>
						<ProfileToolRow
							icon={ListTodo}
							iconBg={theme.palette.orange[100]}
							iconColor={theme.palette.orange[600]}
							label={t(Resources.Settings.Items.Activities)}
							onClick={() => navigate(Routing.ActivitiesList.path())}
						/>
					</ProfileSection>

					<Stack
						bgcolor={theme.palette.background.paper}
						mt={theme.spacingTokens.lg}
					>
						<LogOutRow isLast />
					</Stack>
				</>
			)}
		</MyPlannLayout>
	);
};
